import { sanitiseIdentifier, type PlanLegInput } from '@jupi/shared'
import type { PlanWaypoint, ReportingPoint } from '../../api/plan'

// A new point of the flight plan, from a waypoint or a reporting point.
// A leg is a snapshot (plan.flight_plan_legs), so a reporting point needs
// no waypoint row: it goes in with waypointCode null.

// A pasted-in point starts with calm winds: the pilot adds the forecast.
export function legFromWaypoint(waypoint: PlanWaypoint): PlanLegInput {
  return {
    waypointCode: waypoint.code,
    code: waypoint.code,
    name: waypoint.name,
    lat: waypoint.lat,
    lon: waypoint.lon,
    variation: waypoint.variation,
    com: waypoint.com,
    gnd: waypoint.gnd,
    atis: waypoint.atis,
    area: waypoint.area,
    vor: waypoint.vor,
    ndb: null,
    country: waypoint.country,
    link: waypoint.link,
    windDir: 0,
    windSpeedKt: 0,
    tasKt: null,
  }
}

function nearestWaypoint(waypoints: PlanWaypoint[], lat: number, lon: number, accept: (w: PlanWaypoint) => boolean = () => true) {
  let best: { d: number; waypoint: PlanWaypoint } | null = null
  const cosLat = Math.cos((lat * Math.PI) / 180)
  for (const w of waypoints) {
    if (!accept(w)) continue
    const d = (w.lat - lat) ** 2 + ((w.lon - lon) * cosLat) ** 2
    if (!best || d < best.d) best = { d, waypoint: w }
  }
  return best?.waypoint ?? null
}

/**
 * The AIP gives a reporting point no magnetic variation, and the navigation
 * log would otherwise treat it as 0°. Borrow the nearest waypoint's, which
 * is what a pilot reading the chart would do.
 */
export function nearestVariation(waypoints: PlanWaypoint[], lat: number, lon: number): number | null {
  return nearestWaypoint(waypoints, lat, lon, (w) => w.variation !== null)?.variation ?? null
}

/** A point with no country of its own is in that of the nearest waypoint. */
export function nearestCountry(waypoints: PlanWaypoint[], lat: number, lon: number): string {
  return nearestWaypoint(waypoints, lat, lon)?.country ?? 'fi'
}

export function reportingPointName(point: ReportingPoint): string | null {
  if (point.kind === 'vrp') return [point.aerodrome, 'VRP', point.name].filter(Boolean).join(' ')
  return point.routes ? `ENR 4.4 · ${point.routes}` : 'ENR 4.4'
}

export function legFromReportingPoint(point: ReportingPoint, waypoints: PlanWaypoint[]): PlanLegInput {
  return {
    waypointCode: null,
    code: point.code,
    name: reportingPointName(point),
    lat: point.lat,
    lon: point.lon,
    variation: nearestVariation(waypoints, point.lat, point.lon),
    com: null,
    gnd: null,
    atis: null,
    area: null,
    vor: null,
    ndb: null,
    country: nearestCountry(waypoints, point.lat, point.lon),
    link: null,
    windDir: 0,
    windSpeedKt: 0,
    tasKt: null,
  }
}

/** Code or name, case-insensitive, anywhere in either; codes first. */
export function searchReportingPoints(points: ReportingPoint[], q: string, limit = 20): ReportingPoint[] {
  const needle = q.trim().toUpperCase()
  if (!needle) return []
  const hits = points.filter((p) => p.code.includes(needle) || p.name?.toUpperCase().includes(needle))
  const rank = (p: ReportingPoint) => (p.code === needle ? 0 : p.code.startsWith(needle) ? 1 : 2)
  // eslint-disable-next-line unicorn/no-array-sort -- sorts the fresh array filter() just built
  return hits.sort((a, b) => rank(a) - rank(b) || a.code.localeCompare(b.code)).slice(0, limit)
}

/** The name legFromReportingPoint gives an AIP point: 'ENR 4.4 · Y1', or 'EFHV VRP SIERRA'. */
const isReportingPointName = (name: string) => /^ENR 4\.4|(^|\s)VRP(\s|$)/.test(name)

/**
 * Only a point the pilot made is theirs to label: a waypoint or reporting
 * point keeps its published code. Their description is the leg's name.
 */
export function isUserPoint(leg: { waypointCode?: string | null; name?: string | null }): boolean {
  return !leg.waypointCode && !(leg.name && isReportingPointName(leg.name))
}

/**
 * How a leg is shown: its code, and the text under it. A point the pilot made
 * before codes were limited to the GPS's five characters shows the short form,
 * with what they wrote beneath.
 */
export function legLabel(leg: { code: string; waypointCode?: string | null; name?: string | null }): {
  code: string
  name: string | null
} {
  const short = sanitiseIdentifier(leg.code)
  if (!isUserPoint(leg) || (short === leg.code && leg.code)) return { code: leg.code, name: leg.name ?? null }
  return { code: short || 'WPT', name: leg.name || leg.code }
}

/** A point dropped on empty map: a leg with no waypoint row, named for its place in the plan. */
export function legFromPosition(lat: number, lon: number, waypoints: PlanWaypoint[], taken: string[]): PlanLegInput {
  let n = 1
  while (taken.includes(`WP${n}`)) n++
  return {
    waypointCode: null,
    code: `WP${n}`,
    name: null,
    lat: Math.round(lat * 1e6) / 1e6,
    lon: Math.round(lon * 1e6) / 1e6,
    variation: nearestVariation(waypoints, lat, lon),
    com: null,
    gnd: null,
    atis: null,
    area: null,
    vor: null,
    ndb: null,
    country: nearestCountry(waypoints, lat, lon),
    link: null,
    windDir: 0,
    windSpeedKt: 0,
    tasKt: null,
  }
}

/** The plan flown the other way round; each point keeps its own wind. */
export function reverseLegs<T>(legs: T[]): T[] {
  // eslint-disable-next-line unicorn/no-array-reverse -- reverses the copy
  return [...legs].reverse()
}

const EARTH_RADIUS_NM = 3440.065

/** A ring of `steps` points `radiusNm` from the centre, as GeoJSON positions (closed). */
export function circleRing(lat: number, lon: number, radiusNm: number, steps = 90): [number, number][] {
  const d = radiusNm / EARTH_RADIUS_NM
  const lat1 = (lat * Math.PI) / 180
  const lon1 = (lon * Math.PI) / 180
  const ring: [number, number][] = []
  for (let i = 0; i <= steps; i++) {
    const bearing = (2 * Math.PI * (i % steps)) / steps
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing))
    const lon2 =
      lon1 +
      Math.atan2(Math.sin(bearing) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2))
    ring.push([(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI])
  }
  return ring
}

const mercatorY = (latDeg: number) => Math.log(Math.tan(Math.PI / 4 + (latDeg * Math.PI) / 360))

/**
 * Where a leg's direction arrow goes: the middle of the line as the map draws
 * it (straight in Web Mercator), and the bearing it runs, degrees from north.
 */
export function legArrows(route: { lat: number; lon: number }[]): { lat: number; lon: number; bearing: number }[] {
  const arrows: { lat: number; lon: number; bearing: number }[] = []
  for (let i = 0; i < route.length - 1; i++) {
    const a = route[i]!
    const b = route[i + 1]!
    const dx = ((b.lon - a.lon) * Math.PI) / 180
    const dy = mercatorY(b.lat) - mercatorY(a.lat)
    if (dx === 0 && dy === 0) continue
    const midY = (mercatorY(a.lat) + mercatorY(b.lat)) / 2
    arrows.push({
      lat: ((2 * Math.atan(Math.exp(midY)) - Math.PI / 2) * 180) / Math.PI,
      lon: (a.lon + b.lon) / 2,
      bearing: ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360,
    })
  }
  return arrows
}
